import {
  Button,
  Checkbox,
  Group,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
  Title,
} from "@assetwatch/ui";
import type { Dispatch, SetStateAction } from "react";
import {
  PROCEDURE_ITEM_TYPES,
  type ProcedureAuthoringDraft,
  type ProcedureItem,
  updateItem,
} from "./procedureAuthoring";

type ProcedurePropertiesPanelProps = {
  draft: ProcedureAuthoringDraft;
  errors: Record<string, string>;
  selectedItem: ProcedureItem | null;
  selectedStructure: { kind: "heading" | "section"; id: string } | null;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
  addChoice: (itemId: string) => void;
  removeChoice: (itemId: string, optionIndex: number) => void;
};

export function ProcedurePropertiesPanel({
  draft,
  errors,
  selectedItem,
  selectedStructure,
  setDraft,
  addChoice,
  removeChoice,
}: ProcedurePropertiesPanelProps) {
  return (
    <div className="min-w-0 lg:sticky lg:top-(--spacing-16) lg:self-start">
      <Tile padding="none" width="full">
        <div className="border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12)">
          <Title as="h2" size="lg">
            Properties
          </Title>
        </div>
        <div className="p-(--spacing-20)">
          <Stack gap={16}>
            {selectedItem ? (
              <>
                <Textarea
                  error={errors[selectedItem.id]}
                  id={`procedure-item-label-${selectedItem.id}`}
                  label="Label"
                  onChange={(event) =>
                    setDraft((current) =>
                      updateItem(current, selectedItem.id, (item) => ({
                        ...item,
                        label: event.target.value,
                      })),
                    )
                  }
                  placeholder="Step name"
                  rows={2}
                  value={selectedItem.label}
                />
                <Textarea
                  label="Description (optional)"
                  onChange={(event) =>
                    setDraft((current) =>
                      updateItem(current, selectedItem.id, (item) => ({
                        ...item,
                        description: event.target.value,
                      })),
                    )
                  }
                  placeholder="Extra guidance shown to the technician"
                  rows={2}
                  value={selectedItem.description}
                />
                <Select
                  label="Step type"
                  onChange={(value) => {
                    if (!value) return;
                    setDraft((current) =>
                      updateItem(current, selectedItem.id, (item) => ({
                        ...item,
                        type: value as ProcedureItem["type"],
                        options:
                          value === "choice"
                            ? item.options.length > 0
                              ? item.options
                              : ["Option 1"]
                            : [],
                      })),
                    );
                  }}
                  options={PROCEDURE_ITEM_TYPES.map(({ value, label }) => ({
                    value,
                    label,
                  }))}
                  value={selectedItem.type}
                />
                <Checkbox
                  checked={selectedItem.required}
                  label="Required to complete"
                  onCheckedChange={(checked) =>
                    setDraft((current) =>
                      updateItem(current, selectedItem.id, (item) => ({
                        ...item,
                        required: checked === true,
                      })),
                    )
                  }
                />
                {selectedItem.type === "number" ? (
                  <Input
                    label="Unit"
                    onChange={(event) =>
                      setDraft((current) =>
                        updateItem(current, selectedItem.id, (item) => ({
                          ...item,
                          unit: event.target.value,
                        })),
                      )
                    }
                    value={selectedItem.unit}
                  />
                ) : null}
                {selectedItem.type === "choice" ? (
                  <Stack gap={4}>
                    <Text>
                      {selectedItem.options.length > 1 ? "Choices" : "Option"}
                    </Text>
                    {errors[selectedItem.id] ? (
                      <Text tone="negative">{errors[selectedItem.id]}</Text>
                    ) : null}
                    {selectedItem.options.map((option, index) => (
                      <Group
                        align="center"
                        gap={8}
                        key={`${selectedItem.id}-${index}`}
                      >
                        <Input
                          aria-label={`Choice ${index + 1}`}
                          onChange={(event) =>
                            setDraft((current) =>
                              updateItem(current, selectedItem.id, (item) => ({
                                ...item,
                                options: item.options.map(
                                  (currentOption, optionIndex) =>
                                    optionIndex === index
                                      ? event.target.value
                                      : currentOption,
                                ),
                              })),
                            )
                          }
                          value={option}
                        />
                        {selectedItem.options.length > 1 ? (
                          <IconButton
                            aria-label={`Remove choice ${index + 1}`}
                            icon="trash-delete-bin"
                            intent="destructive"
                            onClick={() => removeChoice(selectedItem.id, index)}
                            size="sm"
                            variant="ghost"
                          />
                        ) : null}
                      </Group>
                    ))}
                    <Button
                      onClick={() => addChoice(selectedItem.id)}
                      size="sm"
                      variant="outline"
                    >
                      Add choice
                    </Button>
                  </Stack>
                ) : null}
              </>
            ) : selectedStructure?.kind === "heading" ? (
              <Stack gap={8}>
                <Input
                  label="Heading label"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      groups: current.groups.map((group) =>
                        group.id === selectedStructure.id
                          ? { ...group, title: event.target.value }
                          : group,
                      ),
                    }))
                  }
                  value={
                    draft.groups.find(
                      (group) => group.id === selectedStructure.id,
                    )?.title ?? ""
                  }
                />
              </Stack>
            ) : selectedStructure?.kind === "section" ? (
              <Stack gap={8}>
                <Input
                  label="Section label"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      looseSections: current.looseSections.map((section) =>
                        section.id === selectedStructure.id
                          ? { ...section, title: event.target.value }
                          : section,
                      ),
                      groups: current.groups.map((group) => ({
                        ...group,
                        sections: group.sections.map((section) =>
                          section.id === selectedStructure.id
                            ? { ...section, title: event.target.value }
                            : section,
                        ),
                      })),
                    }))
                  }
                  value={
                    draft.looseSections.find(
                      (section) => section.id === selectedStructure.id,
                    )?.title ??
                    draft.groups
                      .flatMap((group) => group.sections)
                      .find((section) => section.id === selectedStructure.id)
                      ?.title ??
                    ""
                  }
                />
              </Stack>
            ) : (
              <Text tone="subtle">Select an item to edit its properties.</Text>
            )}
          </Stack>
        </div>
      </Tile>
    </div>
  );
}
